import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { fetchEventTarget } from '@/features/events/api';
import { fetchStaff } from '@/features/staff/api';
import { fetchSurveyResponses, type SurveyResponse } from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

/**
 * 提出状況の一覧（氏名・状態・提出日時）。
 *
 * 提出日は事務局が督促するかどうかの判断に使う情報なので、モーダルの中ではなく
 * タブを開いた時点で見えるようにする（D-314）。回答本文も同じタブ内（D-340）。
 *
 * 回答の行は回答者がそのアンケートを開いた時点で作られるため、一度も開いていない人は
 * 行が存在しない。事務局には対象名簿と突き合わせて「未着手」として並べる
 * （行のある人だけ出すと、出していない人が一覧から消えて督促漏れになる）。
 *
 * 受講生向けは EventTarget、理事・講師向けは有効な理事・講師一覧（D-349）。
 */

type Row = {
  key: string;
  memberId: string | null;
  name: string;
  state: '提出済み' | '再提出' | '下書き保存中' | '未着手';
  submittedAt: string | null;
  savedAt: string | null;
  isOverdue: boolean;
};

const STATE_CLASS: Record<Row['state'], string> = {
  提出済み: 'bg-wpnotice-success/20 text-wptext-strong',
  再提出: 'bg-wpnotice-success/20 text-wptext-strong',
  下書き保存中: 'bg-wpcanvas text-wptext-strong',
  未着手: 'bg-wpnotice-warning/20 text-wptext-strong',
};

function stateOf(response: SurveyResponse): Row['state'] {
  if (response.status === 'submitted') return '提出済み';
  if (response.status === 'resubmitted') return '再提出';
  return '下書き保存中';
}

export function SurveySubmissionStatus({
  eventId,
  eventSurveyId,
  canSeeTargets,
  respondentRole = 'member',
}: {
  eventId: string;
  eventSurveyId: string;
  /** 対象者名簿を引けるか（事務局のみ）。引けないときは回答のある人だけを並べる */
  canSeeTargets: boolean;
  /** member=受講生対象 / director=理事・講師全員（D-349） */
  respondentRole?: 'member' | 'director';
}): ReactElement | null {
  const responses = useQuery({
    queryKey: ['survey-responses', eventSurveyId],
    queryFn: () => fetchSurveyResponses(eventSurveyId),
  });
  const target = useQuery({
    queryKey: ['event-target', eventId],
    queryFn: () => fetchEventTarget(eventId),
    enabled: canSeeTargets && respondentRole === 'member',
  });
  const directors = useQuery({
    queryKey: ['staff', 'director'],
    queryFn: () => fetchStaff({ role: 'director', includeInactive: false }),
    enabled: canSeeTargets && respondentRole === 'director',
  });

  if (responses.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (responses.isError) {
    // 参照権限が無いロールには、この欄そのものを出さない
    if (responses.error instanceof ApiError && responses.error.status === 403) return null;
    return <p className="text-wpnotice-error">提出状況の読み込みに失敗しました。</p>;
  }

  const rows: SurveyResponse[] = responses.data;
  // 匿名アンケートは誰が出したかを返さない。氏名を並べず件数だけ伝える
  // 理事向けは member_id が常に null なので respondent_user_id の有無で判定する
  const anonymous =
    rows.length > 0 &&
    rows.every((r) => r.member_id === null && r.respondent_user_id === null);
  if (anonymous) {
    const submitted = rows.filter(
      (r) => r.status === 'submitted' || r.status === 'resubmitted',
    ).length;
    return (
      <p className="text-wptext-muted">
        匿名のアンケートのため、誰がいつ出したかは表示しません（提出 {submitted}件 / 回答 {rows.length}件）。
      </p>
    );
  }

  const merged: Row[] = [];

  if (respondentRole === 'director') {
    const byUser = new Map(
      rows
        .filter((r) => r.respondent_user_id)
        .map((r) => [r.respondent_user_id as string, r]),
    );
    if (canSeeTargets && directors.data) {
      for (const staff of directors.data) {
        const response = byUser.get(staff.user_id);
        merged.push({
          key: staff.user_id,
          memberId: null,
          name: staff.display_name ?? staff.email,
          state: response ? stateOf(response) : '未着手',
          submittedAt: response?.submitted_at ?? null,
          savedAt: response?.draft_saved_at ?? null,
          isOverdue: response?.is_overdue ?? false,
        });
        byUser.delete(staff.user_id);
      }
    }
    for (const response of byUser.values()) {
      merged.push({
        key: response.id,
        memberId: null,
        name: response.respondent_name ?? response.respondent_user_id ?? '（不明）',
        state: stateOf(response),
        submittedAt: response.submitted_at,
        savedAt: response.draft_saved_at,
        isOverdue: response.is_overdue,
      });
    }
  } else {
    const byMember = new Map(rows.filter((r) => r.member_id).map((r) => [r.member_id as string, r]));
    if (canSeeTargets && target.data) {
      for (const member of target.data.members) {
        // EventTargetMember の `id` が会員のUUID、`member_id` は画面表示用の会員No.。
        // 回答側（SurveyResponse.member_id）はUUIDなので、突き合わせは `id` で行う
        const response = byMember.get(member.id);
        merged.push({
          key: member.id,
          memberId: member.id,
          name: member.name,
          state: response ? stateOf(response) : '未着手',
          submittedAt: response?.submitted_at ?? null,
          savedAt: response?.draft_saved_at ?? null,
          isOverdue: response?.is_overdue ?? false,
        });
        byMember.delete(member.id);
      }
    }
    // 対象者名簿に載っていない回答（公開後に対象から外れた等）も落とさず出す
    for (const response of byMember.values()) {
      merged.push({
        key: response.id,
        memberId: response.member_id,
        name: response.member_name ?? response.member_id ?? '（不明）',
        state: stateOf(response),
        submittedAt: response.submitted_at,
        savedAt: response.draft_saved_at,
        isOverdue: response.is_overdue,
      });
    }
  }

  if (merged.length === 0) {
    return <p className="text-wptext-muted">対象者がいません。</p>;
  }

  const submittedCount = merged.filter(
    (r) => r.state === '提出済み' || r.state === '再提出',
  ).length;

  return (
    <div>
      <p className="mb-2 text-wptext-muted">
        提出 <span className="font-semibold text-wptext-strong">{submittedCount}</span> / 対象{' '}
        {merged.length}名
        {!canSeeTargets && '（回答を開いた人のみ）'}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-wpborder bg-wpcanvas/60">
              <th className="px-3 py-2">氏名</th>
              <th className="px-3 py-2">状態</th>
              <th className="px-3 py-2">提出日時</th>
            </tr>
          </thead>
          <tbody>
            {merged.map((row) => (
              <tr key={row.key} className="border-b border-wpborder last:border-b-0">
                <td className="px-3 py-2">
                  {row.memberId ? (
                    <Link
                      to={`/members/${row.memberId}/events/${eventId}`}
                      className="text-wpaccent hover:underline"
                    >
                      {row.name}
                    </Link>
                  ) : (
                    row.name
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded-sm px-2 py-0.5 text-xs ${STATE_CLASS[row.state]}`}>
                    {row.state}
                  </span>
                  {row.isOverdue && (
                    <span className="ml-2 text-xs text-wpnotice-error">期限超過</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.submittedAt ? (
                    formatDateTime(row.submittedAt)
                  ) : row.savedAt ? (
                    <span className="text-wptext-muted">
                      未提出（下書き {formatDateTime(row.savedAt)}）
                    </span>
                  ) : (
                    <span className="text-wptext-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
